/**
 * BreakawayBacktestDialog — admin-only. Replays a past trading day through
 * the Breakaway rules (POST /api/admin/breakaway/backtest?date=) and shows
 * what the strip would have displayed, plus what happened after the trigger.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtM = (n) => (n ? `${(n / 1e6).toFixed(1)}M` : '—');
const fmtEt = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '—');
const pct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const pctCls = (v) => (v == null ? '' : v >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400');

function prevWeekdayIso() {
  const d = new Date();
  do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
  return d.toISOString().slice(0, 10);
}

export default function BreakawayBacktestDialog({ open, onOpenChange, universe = 'dividend' }) {
  const [date, setDate] = useState(prevWeekdayIso);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const fetchResult = async (d) => {
    const { data } = await axios.get(`${API}/admin/breakaway/backtest`, { params: { date: d }, withCredentials: true });
    return data;
  };

  useEffect(() => {
    if (!open) return;
    fetchResult(date).then((d) => setResult(d.result)).catch(() => setResult(null));
  }, [open, date]);

  const run = async () => {
    if (busy) return;
    setBusy(true); setProgress('starting…');
    try {
      await axios.post(`${API}/admin/breakaway/settings`, { universe }, { withCredentials: true });
      await axios.post(`${API}/admin/breakaway/backtest`, null, { params: { date }, withCredentials: true });
      const poll = setInterval(async () => {
        try {
          const d = await fetchResult(date);
          setProgress(d.state?.progress || null);
          if (!d.state?.in_progress) {
            clearInterval(poll); setBusy(false); setProgress(null); setResult(d.result);
            if (d.state?.error) toast.error(`Backtest failed: ${d.state.error}`);
            else toast.success(`Backtest ${date}: ${d.result?.picks?.length ?? 0} pick(s), ${d.result?.tracked ?? 0} tracked`);
          }
        } catch { clearInterval(poll); setBusy(false); }
      }, 2000);
    } catch { toast.error('Backtest failed to start'); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="breakaway-backtest-dialog">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle>Breakaway backtest</DialogTitle>
          <DialogDescription>
            Replays that day&apos;s minute bars through the live rules (5-min scan clock, 15-min data delay, 3× minute volume,
            $5–$15, gap-up, float ≤ 20M, news since prior close). Peak / 10:15 / close are measured from the trigger price.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="breakaway-backtest-date"
          />
          <button
            type="button"
            onClick={run}
            disabled={busy || !date}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
            data-testid="breakaway-backtest-run"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {busy ? (progress || 'running…') : 'Run backtest'}
          </button>
          {result && (
            <span className="text-xs text-muted-foreground" data-testid="breakaway-backtest-summary">
              {result.universe === 'all' ? 'Full universe' : 'Dividend universe'} · {result.in_universe} in universe · {result.screened} screened
              (day volume ≥ 1.5× avg) · {result.tracked} tracked · <strong>{result.picks?.length || 0} picks</strong>
            </span>
          )}
        </div>

        {result && (
          <>
            <h4 className="text-sm font-bold mt-3 text-emerald-700 dark:text-emerald-400">Picks — would have shown on the strip</h4>
            {(result.picks || []).length === 0
              ? <p className="text-sm text-muted-foreground" data-testid="breakaway-backtest-no-picks">No stock passed all five checks on {result.date}.</p>
              : <ResultTable rows={result.picks} picks />}
            <h4 className="text-sm font-bold mt-4 text-muted-foreground">Near misses — tripped 3× volume but failed a check</h4>
            {(result.near_misses || []).length === 0
              ? <p className="text-sm text-muted-foreground">None.</p>
              : <ResultTable rows={result.near_misses} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultTable({ rows, picks = false }) {
  return (
    <div className="overflow-x-auto mt-1">
      <table className="w-full text-xs" data-testid={picks ? 'breakaway-backtest-picks' : 'breakaway-backtest-near'}>
        <thead className="text-muted-foreground text-left">
          <tr className="[&>th]:py-1 [&>th]:pr-2 [&>th]:font-semibold">
            <th>Ticker</th><th>Tracked</th><th>Trigger $</th><th>Min RVOL</th><th>Gap</th><th>Float</th>
            <th>News</th><th>Peak</th><th>@10:15</th><th>Close</th>{!picks && <th>Failed</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ticker} className="border-t border-border/50 [&>td]:py-1 [&>td]:pr-2 align-top" data-testid={`breakaway-backtest-row-${r.ticker}`}>
              <td className="font-bold">{r.ticker}{r.pays_dividend && <span className="ml-1 text-[9px] px-1 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">DIV</span>}</td>
              <td className="font-mono">{fmtEt(r.first_tracked)}</td>
              <td className="font-mono">${Number(r.trigger_price).toFixed(2)}</td>
              <td className="font-mono">{r.trigger_rvol}× <span className="text-muted-foreground">(max {r.max_minute_rvol}×)</span></td>
              <td className={`font-mono ${pctCls(r.gap_pct_at_trigger)}`}>{pct(r.gap_pct_at_trigger)}{r.full_gap ? ' ●' : ''}</td>
              <td className="font-mono">{fmtM(r.float_shares)}</td>
              <td className="max-w-[260px]">
                {r.news
                  ? <a href={r.news.url} target="_blank" rel="noopener noreferrer" className="hover:underline line-clamp-2" title={`${r.news.publisher} · ${r.news.source}`}>
                      <span className="font-mono text-muted-foreground">{fmtEt(r.news.published_utc)}</span> {r.news.title}
                    </a>
                  : <span className="text-muted-foreground">none in window</span>}
              </td>
              <td className={`font-mono ${pctCls(r.peak_pct)}`}>{pct(r.peak_pct)}</td>
              <td className={`font-mono ${pctCls(r.pct_1015)}`}>{pct(r.pct_1015)}</td>
              <td className={`font-mono ${pctCls(r.pct_close)}`}>{pct(r.pct_close)}</td>
              {!picks && (
                <td className="text-rose-600 dark:text-rose-400">
                  {[!r.float_ok && (r.float_shares ? `float ${fmtM(r.float_shares)}` : 'no float data'), !r.news_ok && 'no news'].filter(Boolean).join(' · ')}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
